import { Fragment, useCallback, useEffect, useState } from "react";
import {
  Dialog as HuiDialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { DiscardChangesDialog, useDialogCloseGuard } from "@/components/ui/dialogCloseGuard";
import { useModalStackLayer } from "@/lib/modalStack";
import { Spinner } from "@/components/ui/Spinner";
import { Alert } from "@/components/ui/Alert";
import { HeroHeaderLogo } from "@/components/dataTable";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import { canViewHref } from "@/lib/acl";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  useCurrentUser,
  getUserLocalLogin,
  loginsMatch,
  userHasGroup,
  userIsBuero,
  userIsManagement,
} from "@/lib/auth";
import { isElectronWithOpenFolder } from "@/lib/electron";
import { HERO_PROJECT_BASE } from "@/lib/home";
import { openOrderFolder } from "@/lib/projects";
import {
  AUFTRAG_AUTOCOMPLETE_EMPTY,
  AUFTRAG_AUTOCOMPLETE_PLACEHOLDER,
  auftragLookupToOption,
  loadAuftragAutocompleteOptions,
} from "@/lib/auftragAutocomplete";
import type { AiAnalysisResult, AuftragLookup, MaPositionInput } from "@/lib/material";
import { analyzeImageWithAi } from "@/lib/material";
import { getOrder, getOrderNotes, type ProjectNote } from "@/lib/projectOverview";
import type { Order } from "@/lib/projects";
import { NotesSection } from "@/projectOverview/sections/NotesSection";
import { AiSuggestionsPanel } from "@/pages/material/AiSuggestionsPanel";
import { Lightbox } from "@/pages/material/Lightbox";
import { MaDialog } from "@/pages/material/MaDialog";
import { useMaMediaContextMenu } from "@/pages/material/useMaMediaContextMenu";
import { imageElementFromContextEvent } from "@/lib/clipboardMedia";
import { AgaMobileUploadDialog } from "@/pages/angebotsanfragen/AgaMobileUploadDialog";
import { useDocumentEditor } from "@/pages/dokumente/useDocumentEditor";
import {
  addAgaAttachment,
  addAgaPosition,
  claimAngebotsanfrage,
  createAngebotsanfrage,
  createGdAngebotFromAga,
  deleteAgaAttachment,
  deleteAgaPosition,
  deleteAngebotsanfrage,
  getAgaAttachmentThumbUrl,
  getAgaAttachmentUrl,
  getAngebotsanfrage,
  getProjectContact,
  patchAgaPosition,
  patchAngebotsanfrage,
  setAngebotsanfrageStatus,
  type AgaContactMode,
  type AgaDetail,
  type AgaPosition,
  type AgaPositionInput,
  type AgaStatus,
  type ProjectContact,
} from "@/lib/angebotsanfragen";

export interface AngebotDialogProps {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
  agaId: number | null;
  canProcess: boolean;
  /** Tailwind z-index (z. B. über dem Dokumenteneditor). */
  zIndexClass?: string;
  /** Nested Modals (MA, Confirm, Lightbox) darüber. */
  overlayZIndexClass?: string;
}

interface PendingFile {
  id: string;
  file: File;
  url: string;
}

interface PositionDraft {
  id: number | null;
  localId: string;
  menge: string;
  einheit: string;
  text: string;
  notes: string;
  from_ai: boolean;
  artikel_id: number | null;
  hero_nr: string | null;
  hero_unit: string | null;
  ean: string | null;
  source_ma_position_id: number | null;
}

let LOCAL_ID_COUNTER = 0;
function nextLocalId(prefix: string): string {
  LOCAL_ID_COUNTER += 1;
  return `${prefix}-${LOCAL_ID_COUNTER}-${Math.random().toString(36).slice(2, 8)}`;
}

function makeEmptyPosition(): PositionDraft {
  return {
    id: null,
    localId: nextLocalId("p"),
    menge: "",
    einheit: "",
    text: "",
    notes: "",
    from_ai: false,
    artikel_id: null,
    hero_nr: null,
    hero_unit: null,
    ean: null,
    source_ma_position_id: null,
  };
}

function positionFromDetail(p: AgaPosition): PositionDraft {
  return {
    id: p.id,
    localId: nextLocalId("p"),
    menge: p.menge != null ? String(p.menge) : "",
    einheit: p.einheit ?? "",
    text: p.text ?? "",
    notes: p.notes ?? "",
    from_ai: Boolean(p.from_ai),
    artikel_id: p.artikel_id ?? null,
    hero_nr: p.hero_nr ?? null,
    hero_unit: p.hero_unit ?? null,
    ean: p.ean ?? null,
    source_ma_position_id: p.source_ma_position_id ?? null,
  };
}

function positionToInput(
  p: PositionDraft,
  sortIndex: number,
): AgaPositionInput | null {
  const text = p.text.trim();
  if (!text) return null;
  const menge = parseFloat(p.menge.replace(",", "."));
  return {
    sort_index: sortIndex,
    menge: Number.isFinite(menge) ? menge : null,
    einheit: p.einheit.trim() || null,
    text,
    notes: p.notes.trim() || null,
    from_ai: p.from_ai,
    artikel_id: p.artikel_id,
    hero_nr: p.hero_nr,
    hero_unit: p.hero_unit,
    ean: p.ean,
    source_ma_position_id: p.source_ma_position_id,
  };
}

const STATUS_LABELS: Record<AgaStatus, string> = {
  offen: "offen",
  in_bearbeitung: "in Bearbeitung",
  erledigt: "erledigt",
  abgelehnt: "abgelehnt",
};

function isImage(ct: string | null | undefined): boolean {
  return !!ct && ct.startsWith("image/");
}

function ReadOnlyBlock({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value?: string | null;
  multiline?: boolean;
}) {
  const text = (value || "").trim() || "—";
  return (
    <div className="space-y-1">
      {label ? <div className="eh-label">{label}</div> : null}
      <div
        className={`rounded-ios border border-separator bg-fill-tertiary px-3 py-2 text-sm text-label ${
          multiline ? "whitespace-pre-wrap" : ""
        }`}
      >
        {text}
      </div>
    </div>
  );
}

function projectLabelFromDetail(detail: AgaDetail): string {
  const parts = [
    detail.auftrag_nummer || (detail.auftrag_id ? `#${detail.auftrag_id}` : null),
    detail.auftrag_beschreibung,
    detail.auftrag_ort,
  ].filter(Boolean);
  return parts.join(" · ") || "—";
}

function contactLinesFromDetail(detail: AgaDetail): string {
  if (detail.ansprechpartner_id) {
    return [
      detail.ansprechpartner_name || detail.contact_display,
      [detail.ansprechpartner_company, detail.ansprechpartner_phone, detail.ansprechpartner_email]
        .filter(Boolean)
        .join(" · "),
    ]
      .filter(Boolean)
      .join("\n");
  }
  return [
    detail.contact_name,
    [detail.contact_company, detail.contact_phone, detail.contact_email].filter(Boolean).join(" · "),
  ]
    .filter(Boolean)
    .join("\n");
}

export function AngebotDialog({
  open,
  onClose,
  onChanged,
  agaId,
  canProcess,
  zIndexClass = "z-50",
  overlayZIndexClass = "z-[60]",
}: AngebotDialogProps) {
  const { user } = useCurrentUser();
  const documentEditor = useDocumentEditor();
  const isNew = agaId == null;
  const canProcessMa =
    !!user &&
    (userHasGroup(user, "App-Admins") || userIsBuero(user) || userIsManagement(user));

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creatingAngebot, setCreatingAngebot] = useState(false);
  const [canCreateDokumente, setCanCreateDokumente] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<AgaDetail | null>(null);
  const [maDialogOpen, setMaDialogOpen] = useState(false);
  const [activeMaId, setActiveMaId] = useState<number | null>(null);

  const [project, setProject] = useState<AutocompleteOption<AuftragLookup> | null>(null);
  const [projectContact, setProjectContact] = useState<ProjectContact | null>(null);
  const [contactMode, setContactMode] = useState<AgaContactMode>("project");

  const [contactName, setContactName] = useState("");
  const [contactCompany, setContactCompany] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactNote, setContactNote] = useState("");
  const [notes, setNotes] = useState("");
  const [processorNotes, setProcessorNotes] = useState("");

  const [pending, setPending] = useState<PendingFile[]>([]);
  const [lightbox, setLightbox] = useState<{
    url: string;
    filename: string;
    contentType?: string | null;
  } | null>(null);
  const { openMediaContextMenu, contextMenuNode } = useMaMediaContextMenu();
  const [positions, setPositions] = useState<PositionDraft[]>([]);
  const [projectOrder, setProjectOrder] = useState<Order | null>(null);
  const [projectNotes, setProjectNotes] = useState<ProjectNote[]>([]);

  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState<AiAnalysisResult | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiFileName, setAiFileName] = useState<string | null>(null);
  const [mobileUploadOpen, setMobileUploadOpen] = useState(false);

  useEffect(() => {
    if (!open || !canProcess) {
      setCanCreateDokumente(false);
      return;
    }
    let cancelled = false;
    void canViewHref("/dokumente.html", user?.groups || []).then((ok) => {
      if (!cancelled) setCanCreateDokumente(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [open, canProcess, user?.groups]);

  const myLogin = getUserLocalLogin(user);
  const isClosed =
    detail?.status === "erledigt" || detail?.status === "abgelehnt";
  const isClaimed = !!detail?.bearbeitet_von;
  const isCreator = !!detail && loginsMatch(detail.created_by, myLogin);
  const isClaimer = !!detail && loginsMatch(detail.bearbeitet_von, myLogin);

  const canEditContent =
    isNew || (isCreator && !isClaimed && detail?.status === "offen");
  const showAsText = !isNew && !canEditContent;
  const canEditProcessorNotes = isClaimer && !isClosed;
  const readOnly = !canEditContent;

  const resetForm = useCallback(() => {
    setDetail(null);
    setProject(null);
    setProjectContact(null);
    setContactMode("project");
    setContactName("");
    setContactCompany("");
    setContactPhone("");
    setContactEmail("");
    setContactNote("");
    setNotes("");
    setProcessorNotes("");
    setPositions([]);
    setProjectOrder(null);
    setProjectNotes([]);
    setPending((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.url));
      return [];
    });
    setAiResult(null);
    setAiError(null);
    setAiFileName(null);
    setAiLoading(false);
    setMobileUploadOpen(false);
    setError(null);
  }, []);

  // Detail laden, wenn eine bestehende Anfrage geöffnet wird.
  useEffect(() => {
    if (!open) return;
    if (isNew) {
      resetForm();
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const data = await getAngebotsanfrage(agaId as number);
        if (cancelled) return;
        setDetail(data);
        setNotes(data.notes ?? "");
        setProcessorNotes(data.bearbeitungsnotizen ?? "");
        setContactNote(data.contact_note ?? "");
        setPositions((data.positionen || []).map(positionFromDetail));
        if (data.auftrag_id) {
          setProject(
            auftragLookupToOption({
              id: data.auftrag_id,
              auftragsnummer: data.auftrag_nummer ?? String(data.auftrag_id),
              jahr: null,
              beschreibung: data.auftrag_beschreibung ?? null,
              ort: data.auftrag_ort ?? null,
              ort_zusatz: null,
              kunde: null,
            }),
          );
        }
        if (data.ansprechpartner_id) {
          setContactMode("project");
          setProjectContact({
            auftrag_id: data.auftrag_id ?? 0,
            ansprechpartner_id: data.ansprechpartner_id,
            name: data.ansprechpartner_name ?? null,
            phone: data.ansprechpartner_phone ?? null,
            email: data.ansprechpartner_email ?? null,
            company: data.ansprechpartner_company ?? null,
          });
        } else {
          setContactMode("new");
          setContactName(data.contact_name ?? "");
          setContactCompany(data.contact_company ?? "");
          setContactPhone(data.contact_phone ?? "");
          setContactEmail(data.contact_email ?? "");
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError
              ? err.message
              : "Angebotsanfrage konnte nicht geladen werden.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, agaId, isNew, resetForm]);

  const selectedAuftragId = project?.value?.id ?? null;

  const reloadProjectNotes = useCallback(async () => {
    if (!selectedAuftragId) {
      setProjectOrder(null);
      setProjectNotes([]);
      return;
    }
    try {
      const [order, notes] = await Promise.all([
        getOrder(selectedAuftragId),
        getOrderNotes(selectedAuftragId),
      ]);
      setProjectOrder(order);
      setProjectNotes(notes);
    } catch {
      setProjectOrder(null);
      setProjectNotes([]);
    }
  }, [selectedAuftragId]);

  useEffect(() => {
    void reloadProjectNotes();
  }, [reloadProjectNotes]);

  // Projekt-Ansprechpartner nachladen, wenn ein Projekt gewählt wird.
  useEffect(() => {
    if (!open || !selectedAuftragId) {
      if (!selectedAuftragId) setProjectContact(null);
      return;
    }
    // Bei bestehender Anfrage mit übernommenem AP haben wir die Daten schon.
    let cancelled = false;
    void (async () => {
      try {
        const contact = await getProjectContact(selectedAuftragId);
        if (cancelled) return;
        setProjectContact(contact);
        // Falls das Projekt keinen Ansprechpartner hat, automatisch auf "neu".
        if (!contact.ansprechpartner_id && contactMode === "project" && isNew) {
          setContactMode("new");
        }
      } catch {
        if (!cancelled) setProjectContact(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // contactMode bewusst nicht als Dependency – nur bei Projektwechsel laden.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedAuftragId, isNew]);

  function addEmptyPosition() {
    setPositions((prev) => [...prev, makeEmptyPosition()]);
  }

  function patchPosition(localId: string, patch: Partial<PositionDraft>) {
    setPositions((prev) =>
      prev.map((p) => (p.localId === localId ? { ...p, ...patch } : p)),
    );
  }

  function removePosition(localId: string) {
    setPositions((prev) => prev.filter((p) => p.localId !== localId));
  }

  async function runAiAnalysis(file: File) {
    setAiResult(null);
    setAiError(null);
    setAiFileName(file.name);
    setAiLoading(true);
    try {
      const res = await analyzeImageWithAi(file);
      setAiResult(res);
    } catch (err) {
      setAiError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "KI-Analyse konnte nicht ausgeführt werden.",
      );
    } finally {
      setAiLoading(false);
    }
  }

  function applyAiSuggestions(newPositions: MaPositionInput[]) {
    if (!newPositions.length) {
      setAiResult(null);
      setAiError(null);
      setAiFileName(null);
      return;
    }
    setPositions((prev) => [
      ...prev,
      ...newPositions.map(
        (np): PositionDraft => ({
          id: null,
          localId: nextLocalId("p"),
          menge: np.menge != null ? String(np.menge) : "",
          einheit: np.einheit ?? "",
          text: np.text,
          notes: np.notes ?? "",
          from_ai: true,
          artikel_id: np.artikel_id ?? null,
          hero_nr: np.hero_nr ?? null,
          hero_unit: np.hero_unit ?? null,
          ean: np.ean ?? null,
          source_ma_position_id: null,
        }),
      ),
    ]);
    setAiResult(null);
    setAiError(null);
    setAiFileName(null);
  }

  function dismissAi() {
    setAiResult(null);
    setAiError(null);
    setAiFileName(null);
  }

  function maybeAnalyzeFile(file: File) {
    if (
      file.type.startsWith("image/") &&
      file.type !== "image/heic" &&
      file.type !== "image/heif"
    ) {
      void runAiAnalysis(file);
    }
  }

  function addFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const next: PendingFile[] = [];
    for (const file of Array.from(files)) {
      next.push({
        id: `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        url: URL.createObjectURL(file),
      });
      maybeAnalyzeFile(file);
    }
    setPending((prev) => [...prev, ...next]);
  }

  async function reloadDetail() {
    if (agaId == null) return;
    const refreshed = await getAngebotsanfrage(agaId);
    setDetail(refreshed);
    setPositions((refreshed.positionen || []).map(positionFromDetail));
    onChanged();
  }

  async function handleExistingUpload(files: FileList | null) {
    if (!files || files.length === 0 || agaId == null) return;
    try {
      for (const file of Array.from(files)) {
        await addAgaAttachment(agaId, file);
        maybeAnalyzeFile(file);
      }
      await reloadDetail();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Upload fehlgeschlagen.",
      );
    }
  }

  async function reconcilePositions(targetId: number, drafts: PositionDraft[]) {
    const inputs = drafts
      .map((p, idx) => ({ p, input: positionToInput(p, idx) }))
      .filter((row) => row.input !== null) as {
      p: PositionDraft;
      input: AgaPositionInput;
    }[];

    for (const row of inputs) {
      if (row.p.id) {
        await patchAgaPosition(targetId, row.p.id, row.input);
      } else {
        const res = await addAgaPosition(targetId, row.input);
        row.p.id = res?.id ?? null;
      }
    }

    const keepIds = new Set(
      inputs.filter((r) => r.p.id != null).map((r) => r.p.id as number),
    );
    for (const orig of detail?.positionen || []) {
      if (!keepIds.has(orig.id)) {
        await deleteAgaPosition(targetId, orig.id);
      }
    }
  }

  function removePending(id: string) {
    setPending((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((p) => p.id !== id);
    });
  }

  async function handleDeleteAttachment(attachmentId: number) {
    if (agaId == null) return;
    try {
      await deleteAgaAttachment(agaId, attachmentId);
      const refreshed = await getAngebotsanfrage(agaId);
      setDetail(refreshed);
      onChanged();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Anhang konnte nicht gelöscht werden.",
      );
    }
  }

  function validate(): string | null {
    if (!selectedAuftragId) return "Bitte ein Projekt auswählen.";
    if (contactMode === "project") {
      if (!projectContact?.ansprechpartner_id) {
        return "Das Projekt hat keinen Ansprechpartner – bitte einen neuen anlegen.";
      }
    } else if (!contactName.trim()) {
      return "Bitte den Namen des Ansprechpartners angeben.";
    }
    return null;
  }

  async function handleSave() {
    if (canEditProcessorNotes && !canEditContent) {
      if (agaId == null) return;
      setSaving(true);
      setError(null);
      try {
        const updated = await patchAngebotsanfrage(agaId, {
          bearbeitungsnotizen: processorNotes.trim() || null,
        });
        setDetail(updated);
        setProcessorNotes(updated.bearbeitungsnotizen ?? "");
        onChanged();
        toast.success("Bearbeitungsnotizen gespeichert.");
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : "Notizen konnten nicht gespeichert werden.",
        );
      } finally {
        setSaving(false);
      }
      return;
    }

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const contactPayload =
        contactMode === "project"
          ? {
              contact_mode: "project" as const,
              ansprechpartner_id: projectContact?.ansprechpartner_id ?? null,
              contact_note: contactNote.trim() || null,
            }
          : {
              contact_mode: "new" as const,
              contact_name: contactName.trim() || null,
              contact_company: contactCompany.trim() || null,
              contact_phone: contactPhone.trim() || null,
              contact_email: contactEmail.trim() || null,
              contact_note: contactNote.trim() || null,
            };

      const positionPayload = positions
        .map((p, idx) => positionToInput(p, idx))
        .filter((p): p is AgaPositionInput => p != null);

      if (isNew) {
        const created = await createAngebotsanfrage({
          auftrag_id: selectedAuftragId as number,
          notes: notes.trim() || null,
          positionen: positionPayload,
          ...contactPayload,
        });
        for (const p of pending) {
          try {
            await addAgaAttachment(created.id, p.file);
          } catch {
            toast.error(`Anhang „${p.file.name}" konnte nicht hochgeladen werden.`);
          }
        }
        toast.success(`Angebotsanfrage ${created.aga_nummer} angelegt.`);
      } else {
        await patchAngebotsanfrage(agaId as number, {
          auftrag_id: selectedAuftragId as number,
          notes: notes.trim() || null,
          ...contactPayload,
        });
        await reconcilePositions(agaId as number, positions);
        toast.success("Angebotsanfrage aktualisiert.");
      }
      onChanged();
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Angebotsanfrage konnte nicht gespeichert werden.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleStatus(status: AgaStatus) {
    if (agaId == null) return;
    try {
      const updated = await setAngebotsanfrageStatus(agaId, status);
      setDetail(updated);
      onChanged();
      toast.success(`Status: ${STATUS_LABELS[status]}.`);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Status konnte nicht gesetzt werden.",
      );
    }
  }

  async function handleClaim() {
    if (agaId == null) return;
    try {
      const updated = await claimAngebotsanfrage(agaId);
      setDetail(updated);
      setProcessorNotes(updated.bearbeitungsnotizen ?? "");
      onChanged();
      if (updated.takeover_from_name) {
        toast.info(`Übernommen von ${updated.takeover_from_name}.`);
      } else {
        toast.success("Bearbeitung übernommen.");
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Übernahme fehlgeschlagen.",
      );
    }
  }

  async function handleDelete() {
    if (agaId == null) return;
    if (!window.confirm("Diese Angebotsanfrage wirklich löschen?")) return;
    try {
      await deleteAngebotsanfrage(agaId);
      toast.success("Angebotsanfrage gelöscht.");
      onChanged();
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.",
      );
    }
  }

  async function handleOpenOrCreateAngebot() {
    if (agaId == null) return;
    setCreatingAngebot(true);
    try {
      // Immer über die API gehen: bei Entwürfen ohne Artikelpreise werden
      // Positionen aus der MA/AGA nachgezogen (repaired).
      const result = await createGdAngebotFromAga(agaId);
      if (result.aga) setDetail(result.aga);
      onChanged();
      toast.success(
        result.repaired
          ? "Angebotspositionen aus Artikeldatenbank nachgezogen."
          : result.already_exists
            ? "Vorhandenes Angebot wird geöffnet."
            : "Angebot erstellt.",
      );
      documentEditor.open({ docId: result.gd_angebot_id });
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? err.message
          : "Angebot konnte nicht erstellt werden.",
      );
    } finally {
      setCreatingAngebot(false);
    }
  }

  const projectHasContact = !!projectContact?.ansprechpartner_id;

  const heroProjectId = detail?.hero_project_id ?? projectContact?.hero_project_id ?? null;
  const projectOrdnerpfad = detail?.ordnerpfad ?? projectContact?.ordnerpfad ?? null;
  const projectSharepointPath = detail?.sharepoint_path ?? projectContact?.sharepoint_path ?? null;
  const showFolderOpen = userIsBuero(user) && isElectronWithOpenFolder();
  const hasFolder = Boolean(
    (projectOrdnerpfad || projectSharepointPath || "").trim(),
  );

  async function handleOpenFolder() {
    if (!selectedAuftragId) return;
    await openOrderFolder({
      id: selectedAuftragId,
      ordnerpfad: projectOrdnerpfad,
      sharepoint_path: projectSharepointPath,
    });
  }

  const isBuero = userIsBuero(user);
  const showProjectNotes = Boolean(selectedAuftragId && projectOrder);
  const allowNotesCompose =
    isBuero &&
    canProcess &&
    !isClosed &&
    (isNew || !isClaimed || isClaimer);

  // Ungespeicherte Änderungen erkennen, damit Klick neben das Modal nicht
  // versehentlich Eingaben verwirft.
  const hasUnsaved = (() => {
    if (loading) return false;
    if (isNew) {
      return (
        !!project ||
        pending.length > 0 ||
        contactName.trim() !== "" ||
        contactCompany.trim() !== "" ||
        contactPhone.trim() !== "" ||
        contactEmail.trim() !== "" ||
        contactNote.trim() !== "" ||
        notes.trim() !== ""
      );
    }
    if (!detail) return false;
    if (canEditProcessorNotes && processorNotes !== (detail.bearbeitungsnotizen ?? "")) {
      return true;
    }
    if (!canEditContent) return false;
    if ((project?.value?.id ?? null) !== (detail.auftrag_id ?? null)) return true;
    if (notes !== (detail.notes ?? "")) return true;
    if (contactNote !== (detail.contact_note ?? "")) return true;
    const baselineMode: AgaContactMode = detail.ansprechpartner_id ? "project" : "new";
    if (contactMode !== baselineMode) return true;
    if (contactMode === "new") {
      if (contactName !== (detail.contact_name ?? "")) return true;
      if (contactCompany !== (detail.contact_company ?? "")) return true;
      if (contactPhone !== (detail.contact_phone ?? "")) return true;
      if (contactEmail !== (detail.contact_email ?? "")) return true;
    }
    return false;
  })();

  const { requestClose, confirmOpen, confirmDiscard, cancelDiscard } = useDialogCloseGuard(
    onClose,
    hasUnsaved,
  );
  const { zIndex: stackZIndex, bringToFront } = useModalStackLayer(open);

  return (
    <>
    <Transition show={open} as={Fragment}>
      <HuiDialog
        as="div"
        className={clsx("relative", zIndexClass)}
        style={stackZIndex != null ? { zIndex: stackZIndex } : undefined}
        onClose={requestClose}
        onPointerDownCapture={bringToFront}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-0 sm:items-center sm:p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-150"
              enterFrom="opacity-0 translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:scale-95"
            >
              <DialogPanel className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-t-2xl bg-card shadow-ios-md max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:rounded-none sm:rounded-2xl">
                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-separator px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-5 sm:py-4">
                  <DialogTitle className="text-base font-semibold text-label">
                    {isNew
                      ? "Neue Angebotsanfrage"
                      : detail?.aga_nummer
                        ? `Angebotsanfrage ${detail.aga_nummer}`
                        : "Angebotsanfrage"}
                  </DialogTitle>
                  <div className="flex shrink-0 items-center gap-1">
                    {selectedAuftragId ? (
                      <>
                        {showFolderOpen ? (
                          hasFolder ? (
                            <button
                              type="button"
                              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-separator bg-card text-label transition hover:bg-fill-tertiary"
                              title="Ordner öffnen"
                              aria-label="Ordner öffnen"
                              onClick={() => void handleOpenFolder()}
                            >
                              <span className="text-[1.15rem] leading-none" aria-hidden>
                                📂
                              </span>
                            </button>
                          ) : (
                            <span
                              title="Kein Projektordner hinterlegt"
                              className="inline-flex h-10 w-10 cursor-default items-center justify-center rounded-md border border-separator bg-card opacity-50"
                            >
                              <span className="text-[1.15rem] leading-none grayscale" aria-hidden>
                                📂
                              </span>
                            </span>
                          )
                        ) : null}
                        {heroProjectId ? (
                          <a
                            href={`${HERO_PROJECT_BASE}${encodeURIComponent(String(heroProjectId))}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Hero-Projekt öffnen"
                            aria-label="Hero-Projekt öffnen"
                            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-separator bg-card transition hover:bg-fill-tertiary"
                          >
                            <HeroHeaderLogo className="h-[18px] w-auto object-contain" />
                          </a>
                        ) : (
                          <span
                            title="Kein Hero-Projekt hinterlegt"
                            className="inline-flex h-10 w-10 cursor-default items-center justify-center rounded-md border border-separator bg-card opacity-50"
                          >
                            <HeroHeaderLogo className="h-[18px] w-auto object-contain grayscale" />
                          </span>
                        )}
                      </>
                    ) : null}
                    <button
                      type="button"
                      onClick={requestClose}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full text-label-secondary transition hover:bg-fill hover:text-label"
                      aria-label="Schließen"
                    >
                      <svg viewBox="0 0 20 20" fill="none" aria-hidden className="h-5 w-5">
                        <path d="M6 6l8 8M14 6l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
                  <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
                  {loading ? (
                    <div className="flex items-center justify-center py-10">
                      <Spinner />
                    </div>
                  ) : (
                    <>
                      {error && (
                        <Alert variant="error" title="Fehler">
                          {error}
                        </Alert>
                      )}

                      {!isNew && detail && (
                        <div className="flex flex-wrap items-center gap-2 text-sm text-label-secondary">
                          <span className="inline-flex items-center rounded-full bg-fill-tertiary px-2.5 py-1 font-medium text-label">
                            {STATUS_LABELS[detail.status]}
                          </span>
                          {detail.source_ma_id && detail.source_ma_nummer ? (
                            <button
                              type="button"
                              className="inline-flex items-center rounded-md border border-accent/35 bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent hover:underline"
                              title="Zugehörige Materialanforderung öffnen"
                              onClick={() => {
                                setActiveMaId(detail.source_ma_id as number);
                                setMaDialogOpen(true);
                              }}
                            >
                              MA · {detail.source_ma_nummer}
                            </button>
                          ) : null}
                          <span>
                            erstellt von {detail.created_by_name || detail.created_by}
                          </span>
                          {detail.bearbeitet_von_name && (
                            <span>· bearbeitet von {detail.bearbeitet_von_name}</span>
                          )}
                        </div>
                      )}

                      {showAsText && detail ? (
                        <div className="space-y-4">
                          <ReadOnlyBlock label="Projekt" value={projectLabelFromDetail(detail)} />
                          <ReadOnlyBlock
                            label="Ansprechpartner"
                            value={contactLinesFromDetail(detail)}
                            multiline
                          />
                          <ReadOnlyBlock
                            label="Notiz zum Ansprechpartner"
                            value={detail.contact_note}
                            multiline
                          />
                          <ReadOnlyBlock
                            label="Beschreibung / Freitext"
                            value={detail.notes}
                            multiline
                          />
                        </div>
                      ) : (
                        <>
                          {/* Projekt */}
                          <LockedAutocomplete<AuftragLookup>
                            label="Projekt"
                            required
                            placeholder={AUFTRAG_AUTOCOMPLETE_PLACEHOLDER}
                            emptyText={AUFTRAG_AUTOCOMPLETE_EMPTY}
                            value={project}
                            onChange={setProject}
                            loadOptions={loadAuftragAutocompleteOptions}
                            disabled={readOnly}
                            hint="Zu welchem Projekt gehört die Angebotsanfrage?"
                          />

                          {/* Ansprechpartner */}
                          <div className="space-y-3">
                            <div className="eh-label">Ansprechpartner</div>
                            <div className="flex flex-wrap gap-2">
                              <label
                                className={`inline-flex items-center gap-2 rounded-ios border px-3 py-2 text-sm ${
                                  contactMode === "project"
                                    ? "border-accent bg-accent-soft text-accent"
                                    : "border-separator-opaque text-label"
                                } ${!projectHasContact ? "opacity-50" : "cursor-pointer"}`}
                              >
                                <input
                                  type="radio"
                                  name="aga-contact-mode"
                                  className="sr-only"
                                  checked={contactMode === "project"}
                                  disabled={readOnly || !projectHasContact}
                                  onChange={() => setContactMode("project")}
                                />
                                Aus Projekt übernehmen
                              </label>
                              <label
                                className={`inline-flex cursor-pointer items-center gap-2 rounded-ios border px-3 py-2 text-sm ${
                                  contactMode === "new"
                                    ? "border-accent bg-accent-soft text-accent"
                                    : "border-separator-opaque text-label"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="aga-contact-mode"
                                  className="sr-only"
                                  checked={contactMode === "new"}
                                  disabled={readOnly}
                                  onChange={() => setContactMode("new")}
                                />
                                Neuer Ansprechpartner
                              </label>
                            </div>

                            {contactMode === "project" ? (
                              <div className="rounded-ios border border-separator-opaque bg-fill-tertiary p-3 text-sm">
                                {projectHasContact ? (
                                  <>
                                    <div className="font-medium text-label">
                                      {projectContact?.name || "Ansprechpartner"}
                                    </div>
                                    <div className="text-label-secondary">
                                      {[
                                        projectContact?.company,
                                        projectContact?.phone,
                                        projectContact?.email,
                                      ]
                                        .filter(Boolean)
                                        .join(" · ") || "Keine weiteren Angaben"}
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-label-secondary">
                                    {selectedAuftragId
                                      ? "Für dieses Projekt ist kein Ansprechpartner hinterlegt – bitte einen neuen anlegen."
                                      : "Bitte zuerst ein Projekt auswählen."}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div className="space-y-1">
                                  <label className="eh-label" htmlFor="aga-contact-name">
                                    Name<span className="ml-1 text-status-red">*</span>
                                  </label>
                                  <input
                                    id="aga-contact-name"
                                    className="eh-input"
                                    value={contactName}
                                    disabled={readOnly}
                                    onChange={(e) => setContactName(e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="eh-label" htmlFor="aga-contact-company">
                                    Firma
                                  </label>
                                  <input
                                    id="aga-contact-company"
                                    className="eh-input"
                                    value={contactCompany}
                                    disabled={readOnly}
                                    onChange={(e) => setContactCompany(e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="eh-label" htmlFor="aga-contact-phone">
                                    Telefon
                                  </label>
                                  <input
                                    id="aga-contact-phone"
                                    className="eh-input"
                                    value={contactPhone}
                                    disabled={readOnly}
                                    onChange={(e) => setContactPhone(e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="eh-label" htmlFor="aga-contact-email">
                                    E-Mail
                                  </label>
                                  <input
                                    id="aga-contact-email"
                                    type="email"
                                    className="eh-input"
                                    value={contactEmail}
                                    disabled={readOnly}
                                    onChange={(e) => setContactEmail(e.target.value)}
                                  />
                                </div>
                              </div>
                            )}

                            <div className="space-y-1">
                              <label className="eh-label" htmlFor="aga-contact-note">
                                Notiz zum Ansprechpartner
                              </label>
                              <textarea
                                id="aga-contact-note"
                                className="eh-input min-h-[2.5rem]"
                                rows={2}
                                value={contactNote}
                                disabled={readOnly}
                                onChange={(e) => setContactNote(e.target.value)}
                              />
                            </div>
                          </div>

                          {/* Freitext */}
                          <div className="space-y-1">
                            <label className="eh-label" htmlFor="aga-notes">
                              Beschreibung / Freitext
                            </label>
                            <textarea
                              id="aga-notes"
                              className="eh-input min-h-[6rem]"
                              rows={5}
                              placeholder="Was soll angeboten werden? Details, Maße, Besonderheiten …"
                              value={notes}
                              disabled={readOnly}
                              onChange={(e) => setNotes(e.target.value)}
                            />
                          </div>
                        </>
                      )}

                      {!isNew && isClaimed ? (
                        <div className="space-y-1">
                          <label className="eh-label" htmlFor="aga-processor-notes">
                            Bearbeitungsnotizen
                          </label>
                          {canEditProcessorNotes ? (
                            <textarea
                              id="aga-processor-notes"
                              className="eh-input min-h-[5rem]"
                              rows={4}
                              placeholder="Eigene Notizen zur Bearbeitung …"
                              value={processorNotes}
                              onChange={(e) => setProcessorNotes(e.target.value)}
                            />
                          ) : (
                            <ReadOnlyBlock
                              label=""
                              value={detail?.bearbeitungsnotizen}
                              multiline
                            />
                          )}
                          {detail?.bearbeitet_von_name ? (
                            <p className="text-xs text-label-tertiary">
                              Bearbeitet von {detail.bearbeitet_von_name}
                            </p>
                          ) : null}
                        </div>
                      ) : null}

                      {/* Positionen */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="eh-label">Positionen</div>
                          {canEditContent ? (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={addEmptyPosition}
                            >
                              + Position
                            </Button>
                          ) : null}
                        </div>

                        {(aiLoading || aiResult || aiError) && (
                          <AiSuggestionsPanel
                            loading={aiLoading}
                            result={aiResult}
                            error={aiError}
                            fileName={aiFileName}
                            onApply={applyAiSuggestions}
                            onDismiss={dismissAi}
                          />
                        )}

                        {positions.length === 0 ? (
                          <p className="rounded-ios border border-dashed border-separator-opaque p-3 text-center text-xs text-label-tertiary">
                            Noch keine Positionen. Klicke auf „+ Position“ oder lade ein
                            Bild / eine Skizze hoch – die KI schlägt Positionen vor.
                          </p>
                        ) : (
                          <ul className="space-y-2">
                            {positions.map((p, idx) => (
                              <li
                                key={p.localId}
                                className="rounded-ios border border-separator bg-card p-3 shadow-ios"
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <span className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-label-secondary">
                                    <span>Pos. {idx + 1}</span>
                                    {p.from_ai ? (
                                      <span
                                        className="rounded-full bg-status-orange/15 px-2 py-0.5 text-[11px] font-medium normal-case tracking-normal text-status-orange"
                                        title="Diese Position wurde von der KI erkannt und sollte noch geprüft werden."
                                      >
                                        Von KI erkannt
                                      </span>
                                    ) : null}
                                  </span>
                                  {canEditContent ? (
                                    <button
                                      type="button"
                                      className="text-xs text-status-red hover:underline"
                                      onClick={() => removePosition(p.localId)}
                                    >
                                      Entfernen
                                    </button>
                                  ) : null}
                                </div>
                                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[80px_100px_1fr]">
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    className="eh-input"
                                    placeholder="Menge"
                                    value={p.menge}
                                    disabled={!canEditContent}
                                    onChange={(e) =>
                                      patchPosition(p.localId, { menge: e.target.value })
                                    }
                                  />
                                  <input
                                    type="text"
                                    className="eh-input"
                                    placeholder="Einheit"
                                    value={p.einheit}
                                    disabled={!canEditContent}
                                    onChange={(e) =>
                                      patchPosition(p.localId, { einheit: e.target.value })
                                    }
                                  />
                                  <input
                                    type="text"
                                    className="eh-input"
                                    placeholder="Bezeichnung"
                                    value={p.text}
                                    disabled={!canEditContent}
                                    onChange={(e) =>
                                      patchPosition(p.localId, { text: e.target.value })
                                    }
                                  />
                                </div>
                                {(p.notes || canEditContent) && (
                                  <textarea
                                    className="eh-input mt-2"
                                    rows={1}
                                    placeholder="Notiz (optional)"
                                    value={p.notes}
                                    disabled={!canEditContent}
                                    onChange={(e) =>
                                      patchPosition(p.localId, { notes: e.target.value })
                                    }
                                  />
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      {/* Anhänge */}
                      <div className="space-y-2">
                        <div className="eh-label">Bilder / Anhänge</div>

                        {!isNew && detail && detail.attachments.length > 0 && (
                          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                            {detail.attachments.map((att) => {
                              const fullUrl = getAgaAttachmentUrl(detail.id, att.id);
                              const thumbUrl = getAgaAttachmentThumbUrl(detail.id, att.id);
                              return (
                                <div
                                  key={att.id}
                                  className="group relative overflow-hidden rounded-ios border border-separator-opaque bg-fill-tertiary"
                                >
                                  {isImage(att.content_type) ? (
                                    <button
                                      type="button"
                                      className="block w-full"
                                      onClick={() =>
                                        setLightbox({
                                          url: thumbUrl,
                                          filename: att.filename || "bild.jpg",
                                          contentType: att.content_type,
                                        })
                                      }
                                      onContextMenu={(e) =>
                                        openMediaContextMenu(e, {
                                          url: fullUrl,
                                          filename: att.filename || "bild.jpg",
                                          contentType: att.content_type || "image/jpeg",
                                          isImage: true,
                                          imageElement: imageElementFromContextEvent(
                                            e.target,
                                            e.currentTarget,
                                          ),
                                        })
                                      }
                                    >
                                      <img
                                        src={thumbUrl}
                                        alt={att.filename}
                                        crossOrigin="use-credentials"
                                        className="aspect-square w-full object-cover"
                                      />
                                    </button>
                                  ) : (
                                    <a
                                      href={fullUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="flex aspect-square w-full items-center justify-center p-2 text-center text-xs text-label-secondary"
                                    >
                                      {att.filename}
                                    </a>
                                  )}
                                  {canEditContent && (
                                    <button
                                      type="button"
                                      onClick={() => void handleDeleteAttachment(att.id)}
                                      className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition group-hover:opacity-100"
                                      aria-label="Anhang löschen"
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {pending.length > 0 && (
                          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                            {pending.map((p) => (
                              <div
                                key={p.id}
                                className="group relative overflow-hidden rounded-ios border border-separator-opaque bg-fill-tertiary"
                              >
                                {isImage(p.file.type) ? (
                                  <button
                                    type="button"
                                    className="block w-full"
                                    onClick={() =>
                                      setLightbox({
                                        url: p.url,
                                        filename: p.file.name || "bild.jpg",
                                        contentType: p.file.type,
                                      })
                                    }
                                    onContextMenu={(e) =>
                                      openMediaContextMenu(e, {
                                        file: p.file,
                                        url: p.url,
                                        filename: p.file.name || "bild.jpg",
                                        contentType: p.file.type,
                                        isImage: true,
                                        imageElement: imageElementFromContextEvent(
                                          e.target,
                                          e.currentTarget,
                                        ),
                                      })
                                    }
                                  >
                                    <img
                                      src={p.url}
                                      alt={p.file.name}
                                      className="aspect-square w-full object-cover"
                                    />
                                  </button>
                                ) : (
                                  <div className="flex aspect-square w-full items-center justify-center p-2 text-center text-xs text-label-secondary">
                                    {p.file.name}
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={() => removePending(p.id)}
                                  className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition group-hover:opacity-100"
                                  aria-label="Entfernen"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {canEditContent && (
                          <div className="flex flex-wrap items-center gap-2">
                            <label className="inline-flex cursor-pointer items-center gap-2 rounded-ios border border-dashed border-separator-opaque px-3 py-2 text-sm text-label-secondary transition hover:border-accent hover:text-accent">
                              <span aria-hidden>+</span>
                              <span>Bilder / Dateien hinzufügen</span>
                              <input
                                type="file"
                                accept="image/*,application/pdf"
                                multiple
                                className="hidden"
                                onChange={(e) => {
                                  if (isNew) {
                                    addFiles(e.target.files);
                                  } else {
                                    void handleExistingUpload(e.target.files);
                                  }
                                  e.target.value = "";
                                }}
                              />
                            </label>
                            {!isNew ? (
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => setMobileUploadOpen(true)}
                              >
                                Vom Handy hochladen
                              </Button>
                            ) : (
                              <p className="text-xs text-label-tertiary">
                                Handy-Upload ist nach dem Speichern verfügbar.
                              </p>
                            )}
                          </div>
                        )}
                        <p className="text-xs text-label-tertiary">
                          Hochgeladene Bilder und PDFs werden zusätzlich im Projektordner
                          unter der Angebotsanfrage-Nummer auf SharePoint abgelegt.
                          Fotos können per KI in Positionen umgewandelt werden.
                        </p>
                      </div>
                    </>
                  )}
                  </div>

                  {showProjectNotes && projectOrder && selectedAuftragId ? (
                    <aside className="flex min-h-0 w-full shrink-0 flex-col border-t border-separator bg-card lg:w-[min(22rem,34%)] lg:border-l lg:border-t-0">
                      <div className="min-h-0 flex-1 overflow-hidden p-3">
                        <NotesSection
                          orderId={selectedAuftragId}
                          project={projectOrder}
                          notes={projectNotes}
                          user={user}
                          isBuero={isBuero}
                          allowCompose={allowNotesCompose}
                          onChanged={() => void reloadProjectNotes()}
                        />
                      </div>
                    </aside>
                  ) : null}
                </div>

                {/* Footer */}
                {!loading && (
                  <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-separator px-5 py-4">
                    <div className="flex flex-wrap gap-2">
                      {!isNew && canProcess && detail && detail.status !== "erledigt" && detail.status !== "abgelehnt" && (
                        <>
                          {!detail.bearbeitet_von && (
                            <Button variant="secondary" onClick={() => void handleClaim()}>
                              Übernehmen
                            </Button>
                          )}
                          <Button variant="secondary" onClick={() => void handleStatus("erledigt")}>
                            Erledigt
                          </Button>
                          <Button variant="danger" onClick={() => void handleStatus("abgelehnt")}>
                            Ablehnen
                          </Button>
                        </>
                      )}
                      {!isNew &&
                        canCreateDokumente &&
                        detail &&
                        detail.status !== "abgelehnt" &&
                        detail.auftrag_id && (
                          <Button
                            variant="secondary"
                            loading={creatingAngebot}
                            onClick={() => void handleOpenOrCreateAngebot()}
                          >
                            {detail.gd_angebot_id
                              ? detail.gd_angebot_nummer
                                ? `Angebot ${detail.gd_angebot_nummer}`
                                : "Angebot öffnen"
                              : "Angebot erstellen"}
                          </Button>
                        )}
                      {!isNew && canEditContent && detail?.status === "offen" && (
                        <Button variant="ghost" onClick={() => void handleDelete()}>
                          Löschen
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="secondary" onClick={requestClose}>
                        Schließen
                      </Button>
                      {(canEditContent || canEditProcessorNotes) && (
                        <Button onClick={() => void handleSave()} loading={saving}>
                          {canEditContent
                            ? isNew
                              ? "Anfrage einstellen"
                              : "Speichern"
                            : "Notizen speichern"}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </HuiDialog>
    </Transition>

    <DiscardChangesDialog
      open={confirmOpen}
      onConfirm={confirmDiscard}
      onCancel={cancelDiscard}
      zIndexClass={overlayZIndexClass}
    />

    <AgaMobileUploadDialog
      open={mobileUploadOpen}
      onClose={() => setMobileUploadOpen(false)}
      agaId={agaId}
      onUploaded={() => {
        void reloadDetail().catch(() => undefined);
      }}
    />

    <Lightbox
      url={lightbox?.url ?? null}
      copySource={
        lightbox
          ? {
              url: lightbox.url,
              filename: lightbox.filename,
              contentType: lightbox.contentType || "image/jpeg",
            }
          : null
      }
      onClose={() => setLightbox(null)}
      zIndexClass={overlayZIndexClass}
    />

    <MaDialog
      open={maDialogOpen}
      maId={activeMaId}
      canProcess={canProcessMa}
      zIndexClass={overlayZIndexClass}
      onClose={() => {
        setMaDialogOpen(false);
        setActiveMaId(null);
      }}
      onChanged={onChanged}
    />
    {contextMenuNode}
    </>
  );
}
